import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material//ArrowDropDown';
import { Box, Stack, styled } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { isEmpty } from 'lodash';
import { DocumentReferencesView } from 'api';
import {
  ADD_NEW_DOCUMENT_BUTTON,
  DIALOG_CUSTOM_BUTTON_LABEL,
  DIALOG_DROP_ZONE_TITLE,
  DIALOG_SELECTOR_TITLE,
  DIALOG_TITLE,
  DROP_ZONE_TITLE,
  REQUEST_NEW_DOCUMENT,
  SUBTITLE,
} from 'common/constants/documents';
import { EXPORT_VIEWER_VALUE, LIMITED_VIEWER_VALUE } from 'common/constants/user';
import DropZone from 'components/DropZone';
import { FileSrcProps } from 'components/DropZone/DropZone.props';
import { LayoutContext } from 'context';
import { DocumentsContext } from 'context/DocumentsContext';
import { CreateInformationRequestModal } from 'dashboard409a/pages/Information/CreateInformationRequestModal';
import { getRolePermissions } from 'dashboard409a/pages/Information/utils/rolePermissions';
import { useDocuments } from 'services/hooks';
import { Logger } from 'utilities';
import validFileTypes from '../../validFileTypes';
import { FileAlerts } from '../alerts';
import { DocumentReferencesProps } from './DocumentReferences.props';
import DocumentReferencesFooter from './DocumentReferencesFooter';
import DocumentDialog from '../dialogs/DocumentDialog';
import { ReferencedDocumentsList } from '../lists';
import { CustomIMeasurementDate } from '../lists/ReferencedDocumentsList.props';

const StyledDocumentsContextProvider = styled(DocumentsContext.Provider)({
  height: '100%',
});

const DocumentReferences = ({
  selectedMeasurementDate,
  currentPage = '',
  referenceType,
  referencedFeatureId,
  filesToSave,
  setFilesToSave,
  isDisabled,
  onClick,
  documentSourceProps,
}: DocumentReferencesProps) => {
  const { currentFirmRole, viewOnlyUser } = useContext(LayoutContext);

  // Use props if provided, otherwise fall back to context
  const [openAddNewDocument, setOpenAddNewDocument] = useState<boolean>(false);
  const [openRequestNewDocument, setOpenRequestNewDocument] = useState<boolean>(false);
  const [documents, setDocuments] = useState<DocumentReferencesView[]>([]);
  const [referencedDocuments, setReferencedDocuments] = useState<DocumentReferencesView[]>([]);
  const [droppedFiles, setDroppedFiles] = useState<File[]>([]);
  const [isLoadingReferencedDocuments, setIsLoadingReferencedDocuments] = useState<boolean>(true);
  const fromDocumentFeature = true;
  const fromReferenceWidget = true;
  const {
    getReferencesByMeasurementDateId,
    foundReferencedDocuments,
    currentDocuments,
    getDocuments,
    setCompanyDocuments,
    setCurrentDocuments,
    getFeaturesObjects,
    featuresObjects,
    isLoading,
  } = useDocuments(selectedMeasurementDate);
  const [isOver, setIsOver] = useState<boolean>(false);
  const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);

  const permissions = useMemo(() => getRolePermissions(currentFirmRole ?? 0), [currentFirmRole]);

  const currentPageData = {
    referencedFeatureId: referencedFeatureId ?? 0,
    referenceType: referenceType ?? null,
  };

  const areDocumentsActionsDisabled = useMemo(
    () => [EXPORT_VIEWER_VALUE, LIMITED_VIEWER_VALUE].includes(currentFirmRole as unknown as number),
    [currentFirmRole]
  );

  const handleOpen = useCallback(
    (dialogType: string) => {
      const actionFunctionsDialog: { [key: string]: () => void } = {
        [ADD_NEW_DOCUMENT_BUTTON]: () => setOpenAddNewDocument(true),
        [REQUEST_NEW_DOCUMENT]: () => setOpenRequestNewDocument(true),
      };
      actionFunctionsDialog[dialogType]();
    },
    [setOpenAddNewDocument, setOpenRequestNewDocument]
  );

  const handleClose = () => {
    setOpenAddNewDocument(false);
    setOpenRequestNewDocument(false);
  };

  const handleDocuments = useCallback(async () => {
    if (!selectedMeasurementDate?.id) {
      return;
    }
    setIsLoadingReferencedDocuments(true);
    await getDocuments(selectedMeasurementDate.id);
    const docs = await getReferencesByMeasurementDateId(selectedMeasurementDate.id);
    if (Array.isArray(docs)) {
      setDocuments(docs);
    }
    setIsLoadingReferencedDocuments(false);
    setIsFileUploaded(false);
  }, [getDocuments, getReferencesByMeasurementDateId, selectedMeasurementDate?.id]);

  const refreshDocuments = useCallback(
    () => getDocuments(selectedMeasurementDate?.id),
    [getDocuments, selectedMeasurementDate?.id]
  );

  const updateReferencedDocuments = useCallback(
    (documentsParam: DocumentReferencesView[]) => {
      handleDocuments();
      setReferencedDocuments(foundReferencedDocuments(documentsParam, currentPage, referencedFeatureId));
    },
    [currentPage, foundReferencedDocuments, handleDocuments, referencedFeatureId]
  );

  const processFile = async (fileSrc: FileSrcProps) => {
    setIsOver(false);
    const fileList = fileSrc.files ? Array.from(fileSrc.files) : [];
    setDroppedFiles(prevState => [...prevState, ...fileList]);
  };

  const fetchDocuments = async () => {
    if (selectedMeasurementDate) {
      try {
        await handleDocuments();
      } catch (error) {
        Logger.error('Error fetching documents:', error);
        setIsLoadingReferencedDocuments(false);
      }
    }
  };

  useEffect(() => {
    fetchDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filesToSave]);

  useEffect(() => {
    if (documents) {
      setReferencedDocuments(foundReferencedDocuments(documents, currentPage, referencedFeatureId));
      setIsLoadingReferencedDocuments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documents]);

  useEffect(() => {
    setDroppedFiles([]);
  }, [isFileUploaded]);

  useEffect(() => {
    if (selectedMeasurementDate) {
      setIsLoadingReferencedDocuments(true);
      getFeaturesObjects();
      fetchDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMeasurementDate]);

  const documentListProps = {
    selectedMeasurementDate: selectedMeasurementDate as CustomIMeasurementDate,
    referenceType,
    referencedFeatureId,
    currentPage,
    isDisabled,
    areDocumentsActionsDisabled,
  };

  const documentsContextProps = useMemo(
    () => ({
      documents,
      updateReferencedDocuments,
      currentPage,
      currentDocuments,
      filesToSave,
      setCompanyDocuments,
      setCurrentDocuments,
      featuresObjects,
      refreshDocuments,
      permissions,
    }),
    [
      documents,
      updateReferencedDocuments,
      currentPage,
      currentDocuments,
      filesToSave,
      setCompanyDocuments,
      setCurrentDocuments,
      featuresObjects,
      refreshDocuments,
      permissions,
    ]
  );

  const documentDialogProps = {
    referenceType,
    referencedFeatureId,
    referencedDocuments,
    setDroppedFiles,
    droppedFiles,
    open: openAddNewDocument,
    onClose: handleClose,
    title: DIALOG_TITLE,
    selectorTitle: DIALOG_SELECTOR_TITLE,
    dropZoneTitle: DIALOG_DROP_ZONE_TITLE,
    endIconSelector: <ArrowDropDownIcon />,
    selectedMeasurementDateId: Number(selectedMeasurementDate?.id),
    customButtonLabel: DIALOG_CUSTOM_BUTTON_LABEL,
    isValid: true,
    confirmCancel: true,
    addSelectComponent: true,
    fromReferenceWidget: true,
    isDisabled: true,
  };

  if (!referencedDocuments) {
    return <Skeleton height="54px" style={{ transform: 'none' }} />;
  }

  return (
    <Box sx={{ height: '100%' }}>
      <StyledDocumentsContextProvider value={documentsContextProps}>
        <Stack direction={'column'} spacing={2} marginTop={2} height={'100%'}>
          <div style={{ width: '100%' }}>
            {isOver ? (
              <DropZone
                setFileSrc={processFile}
                titleArray={DROP_ZONE_TITLE}
                subTitle={SUBTITLE}
                maxSizeAllowedInMb={15}
                validFileTypes={validFileTypes}
                fromDocumentFeature={fromDocumentFeature}
                allowMultipleFiles
              />
            ) : (
              <ReferencedDocumentsList
                {...documentListProps}
                documents={referencedDocuments}
                onClick={onClick}
                filesToSave={filesToSave}
                isLoading={isLoadingReferencedDocuments || isLoading}
                documentSourceProps={documentSourceProps}
              />
            )}
            {!isEmpty(droppedFiles) && (
              <div style={{ margin: '1rem 0' }}>
                <FileAlerts
                  droppedFiles={droppedFiles}
                  selectedMeasurementDateId={Number(selectedMeasurementDate?.id)}
                  validFileTypes={validFileTypes}
                  fromReferenceWidget={fromReferenceWidget}
                  setIsFileUploaded={setIsFileUploaded}
                  setFilesToSave={setFilesToSave}
                />
              </div>
            )}
          </div>
          <DocumentReferencesFooter isDisabled={isDisabled || viewOnlyUser} onClickHandler={handleOpen} />
        </Stack>

        <DocumentDialog {...documentDialogProps} isUploadEnabled={false} />
        {openRequestNewDocument && (
          <CreateInformationRequestModal
            isOpen={openRequestNewDocument}
            setIsOpen={setOpenRequestNewDocument}
            currentMeasurementDateId={Number(selectedMeasurementDate?.id)}
            currentCompanyMeasurementDateId={Number(selectedMeasurementDate?.cmd_id)}
            is409a={false}
            currentPageData={currentPageData}
          />
        )}
      </StyledDocumentsContextProvider>
    </Box>
  );
};

export default DocumentReferences;
